// @flow

/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React from 'react';
import {
	AppDialog, AppDialogDescription, AppDialogAction
} from '@ccxi/ccxi-apps/src/stores/dialog';
import appsUtil from './AppsUtil';
import CompatibilityUtil from './CompatibilityUtil';
import uuid from 'uuid';
import {APP_DIALOG, ANALYTICS} from '@ccxi/ccxi-apps/src/utils/enums';
import AppHandlers from '../containers/AppHandlers/AppHandlers';
import {ACTION_IDS} from '../constants/AppPicker/appPickerConstants';
import {AppsPanelSkeleton} from '@ccxi/ccxi-apps/src/stores/skeleton';
import getConfigApps from '../config';
import AppPickerUtil from './AppPicker/AppPickerUtil';
import Link from '@react/react-spectrum/Link';

type Props = {
	appModel: Object
};

/**
 * A class for rendering incompatible dialog
 */
class IncompatibleDialogUtil {
	appDialog: AppDialog;
	renderCompatibilityDesc: Function;
	dialogType: string;
	allAppsIncompatibleDialog: AppDialog;
	onSystemReqClick: Function;
	appPickerIncompatibleDialog: ?Object;
	EOLDialog: ?Object;

	renderCompatibilityDesc = (props: Props) => {
		let compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.incompatible.dialog.msg'}, {appName: props.appModel.appFamilyName});
		if (appsUtil.isAppNotEntitledForEnterpriseUser(props.appModel.sapcode)
			|| appsUtil.shouldShowPermissionReqdForSubstance(props.appModel.sapcode)) {
			compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.pdp.enterprise.user.permission.warning.desc'});
		}
		else if (CompatibilityUtil.isAnyOlderVersionSupported(props.appModel.sapcode)) {
			compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.pdp.errorMessage.updateReqdText.ccweb'}, {appName: props.appModel.appFamilyName});
		}
		else if (props.appModel.sapcode === 'LRCC') {
			compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.incompatible.dialog.lightroom'}, {
				appName: props.appModel.appFamilyName,
				a: str => <Link href={appsUtil.getCCWebURL('LTRM',undefined,true,false)} id='lightroom-classic-download' class='appsLinkBold'>{str}</Link>
			});
		}
		return (<div>{compatibilityDesc}</div>);
	};

	getHeadingText(appModel: Object) {
		if (appsUtil.isAppNotEntitledForEnterpriseUser(appModel.sapcode)
			|| appsUtil.shouldShowPermissionReqdForSubstance(appModel.sapcode)) {
			return appsUtil.intl.formatMessage({id: 'apps.pdp.enterprise.user.permission.warning.title'});
		}
		return appsUtil.intl.formatMessage({id: 'apps.incompatible.dialog.updateReqdHeading'});
	}

	sendDialogAnalytics(analyticObject: Object) {
		AppHandlers.handleAnalytics({componentParams: {source: appsUtil.getCCWebPathData().isPDP ? ANALYTICS.PDP : ''}, analyticObjectList: [analyticObject]});
	}

	onLearnMoreClicked(appModel: Object) {
		const guid = uuid.v4();
		const url = appsUtil.getCCDGoURL(appsUtil.locale, appsUtil.getSystemReqURL(appModel.productId));
		this.sendDialogAnalytics(
			{
				event: {
					workflow: 'Apps',
					type: 'click',
					subtype: 'learn-more',
					guid,
					subcategory: this.getEventSubcategory(),
				},
				content: {
					id: appModel.sapcode,
					name: appModel.appFamilyName,
					category: appModel.platform,
					version: appModel.codexVersion,
					action: url,
					type: this.dialogType,
				},
			}
		);
		window.open(url);
	}

	getEventSubcategory() {
		const {sectionId, isPDP} = appsUtil.getCCWebPathData();
		const sectionFilter = AppsPanelSkeleton.selectedSectionFilter || sectionId;
		return isPDP ? `Categories:${sectionFilter}` : sectionFilter;
	}

	hideDialog() {
		if (!this.appDialog) {
			return;
		}
		this.appDialog.hide();
		this.appDialog = null;
	}

	handleSystemReqClick(appModel: Object) {
		const guid = uuid.v4();
		const url = appsUtil.openExternalLink(appModel.systemRequirementLink, appModel.sapcode, guid);
		appModel.handleIncompatibileAnalytics(appModel.sapCode, appModel.appFamilyName, {guid, subtype:'system-requirements'}, {action: url});
	}

	hideEOLDialog() {
		if (!this.EOLDialog) {
			return;
		}
		this.EOLDialog.hide();
		this.EOLDialog = null;
	}

	hideAppPickerIncompatibleDialog() {
		if (!this.appPickerIncompatibleDialog) {
			return;
		}
		this.appPickerIncompatibleDialog.hide();
		this.appPickerIncompatibleDialog = null;
	}

	renderAllAppsCompatibilityDesc = (props: Props) => {
		const {appFamilyName, sapCode} = props.appModel;
		let compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.incompatible.dialog.msg_any'}, {
			app: appFamilyName,
			span: str => <span className='allapps-systemRequireLink' onClick={() => this.handleSystemReqClick(props.appModel)}>{str}</span>
		});
		if (CompatibilityUtil.isAnyOlderVersionSupported(sapCode)) {
			compatibilityDesc = appsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.incompatible.dialog.msg_latest'}, {
				app: appFamilyName,
				span: str => <span className='allapps-systemRequireLink' onClick={() => this.handleSystemReqClick(props.appModel)}>{str}</span>
			});
		}
		return (<div>{compatibilityDesc}</div>);
	};

	handleAppPickerIncompatibleDialogAction(actionId: string, appModel: Object) {
		const emailId = window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile() && window.adobeIMS.getUserProfile().email || '';
		switch (actionId) {
			case ACTION_IDS.OK :
				appModel.handleIncompatibileAnalytics(appModel.sapCode, appModel.appFamilyName, {subtype:'ok'});
				break;
			case ACTION_IDS.SEND_LINK: {
				const guid = uuid.v4();
				appModel.handleIncompatibileAnalytics(appModel.sapCode, appModel.appFamilyName, {subtype:'send-link-start'});
				const downloadUrl = `${`${getConfigApps('servers').CC}/apps/all/desktop/pdp/${appsUtil.getProductIdFromSapcode(appModel.sapCode)}?action=install&isStarter=true&source=${AppPickerUtil.getSourceQueryParam()}`}?x-product=CCHome&x-product-location=Apps&guid=${guid}`;
				const emailSubject = appsUtil.intl.formatMessage({id: 'apps.email.productEmailSubject'}, {appname: appModel.appFamilyName});
				const emailBody = appsUtil.intl.formatMessage({id: 'apps.email.productEmailBody'}, {appname: appModel.appFamilyName, downloadurl: downloadUrl});
				const emailUrl = `mailto:${emailId}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
				window.open(emailUrl, '_blank');
			}
		}
		this.hideAppPickerIncompatibleDialog();
	}

	/*
		Can be used to render a dialog given the title, text and CTAs
		Note: Only one such dialog should be invoked at any time
	*/
	renderEOLDialog(heading: string, description: string) {
		if (this.EOLDialog) {
			return;
		}
		const actionList = [];
		actionList.push(new AppDialogAction({
			localizedText: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
			ariaLabel: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
			actionId: ACTION_IDS.OK,
			actionType: {
				quiet: false,
				variant: 'primary'
			}
		}));
		const onAction = () => {
			this.hideEOLDialog();
		};
		this.EOLDialog = new AppDialog({
			heading,
			descriptionList : [new AppDialogDescription(
				APP_DIALOG.DESCRIPTION_TYPE.PARAGRAPH,
				description
			)],
			actionList,
			onAction
		});
		this.EOLDialog.show({variant: 'error'});
	}

	renderAppPickerIncompatibleDialog(appModel: Object) {
		if (this.appPickerIncompatibleDialog) {
			return;
		}
		if (appModel.platform === 'desktop') {
			const actionList = [];
			actionList.push(new AppDialogAction({
				localizedText: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
				ariaLabel: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
				actionId: ACTION_IDS.OK,
				actionType: {
					quiet: false,
					variant: 'primary'
				}
			}));
			actionList.push(new AppDialogAction({
				localizedText: appsUtil.intl.formatMessage({id: 'apps.tile.sendlink'}),
				ariaLabel: appsUtil.intl.formatMessage({id: 'apps.tile.sendlink'}),
				actionId: ACTION_IDS.SEND_LINK,
				actionType: {
					quiet: false,
					variant: 'secondary'
				}
			}));
			let heading = 'apps.allAppsAppPicker.incompatible.dialog.updateReqdHeading';
			heading = CompatibilityUtil.isAnyOlderVersionSupported(appModel.sapCode) ? `${heading}.latest` : `${heading}.any`;
			this.appPickerIncompatibleDialog = new AppDialog({
				type: 'incompatible',
				heading: appsUtil.intl.formatMessage({id: heading}, {appName: appModel.appFamilyName}),
				className: 'allapps-app-dialog-error',
				descriptionList : [new AppDialogDescription(
					APP_DIALOG.DESCRIPTION_TYPE.PARAGRAPH,
					<this.renderAllAppsCompatibilityDesc appModel={appModel} />
				)],
				actionList,
				onAction: (actionId) => {this.handleAppPickerIncompatibleDialogAction(actionId, appModel);}
			});
			this.appPickerIncompatibleDialog.show({variant: 'error'});
		}
	}

	renderDialog(appModel: Object) {
		if (this.appDialog) {
			return;
		}
		if (appModel.platform === 'desktop') {
			const actionList = [];
			this.dialogType = appsUtil.isAppNotEntitledForEnterpriseUser(appModel.sapcode)
				|| appsUtil.shouldShowPermissionReqdForSubstance(appModel.sapcode)
				? 'Permission Required' : 'Update Required';
			this.sendDialogAnalytics(
				{
					event: {
						workflow: 'Apps',
						type: 'render',
						subtype: 'dialog',
						subcategory: this.getEventSubcategory(),
					},
					content: {
						id: appModel.sapcode,
						name: appModel.appFamilyName,
						category: appModel.platform,
						version: appModel.codexVersion,
						type: this.dialogType,
					},
				}
			);
			actionList.push(new AppDialogAction({
				localizedText: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
				ariaLabel: appsUtil.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
				actionId: 'ok',
				actionType: {
					quiet: false,
					variant: 'cta'
				}
			}));
			if (!appsUtil.isAppNotEntitledForEnterpriseUser(appModel.sapcode)
				&& !appsUtil.shouldShowPermissionReqdForSubstance(appModel.sapcode)) {
				actionList.push(new AppDialogAction({
					localizedText: appsUtil.intl.formatMessage({id: 'apps.common.learnMore'}),
					ariaLabel: appsUtil.intl.formatMessage({id: 'apps.common.learnMore'}),
					actionId: 'learnMore',
					actionType: {
						quiet: false,
						variant: 'secondary'
					}
				}));
			}
			this.appDialog = new AppDialog({
				type: 'incompatible',
				heading: this.getHeadingText(appModel),
				descriptionList : [new AppDialogDescription(
					APP_DIALOG.DESCRIPTION_TYPE.PARAGRAPH,
					<this.renderCompatibilityDesc appModel={appModel} />
				)],
				actionList,
				onAction: (actionId) => {
					switch (actionId) {
						case 'learnMore':
							this.onLearnMoreClicked(appModel);
							break;
						case 'ok' :
							this.sendDialogAnalytics(
								{
									event: {
										workflow: 'Apps',
										type: 'click',
										subtype: 'ok',
										subcategory: this.getEventSubcategory(),
									},
									content: {
										id: appModel.sapcode,
										name: appModel.appFamilyName,
										category: appModel.platform,
										version: appModel.codexVersion,
										type: this.dialogType,
									},
								}
							);
							this.hideDialog();
					}
				}
			});
			this.appDialog.show({variant: 'error'});
		}
	}
}

export default new IncompatibleDialogUtil();
